import { auth } from '../firebase';
import { Quote } from '../types';
import { API_BASE_URL } from './apiBase';


class UserService {
  private async getAuthToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return await user.getIdToken();
  }

  async getUserProfile() {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return await response.json();
  }

  async createUserProfile(profileData: Record<string, unknown>) {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/profile`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(profileData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  }

  async getMarketQuotes(symbols: string[]): Promise<{quotes: Record<string, Quote>}> {
    const token = await this.getAuthToken();
    const symbolsParam = symbols.join(',');
    // Add timestamp to prevent browser/CDN caching of stale quotes
    const timestamp = Date.now();
    const response = await fetch(`${API_BASE_URL}/market/quotes?symbols=${symbolsParam}&_t=${timestamp}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  }

  async addToWatchlist(symbol: string) {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/watchlist/add`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ symbol }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  }

  async removeFromWatchlist(symbol: string) {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/watchlist/remove`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ symbol }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  }

  // ============================================================
  // POSITIONS
  // ============================================================
  
  async getPositions(status: 'open' | 'closed' | 'all' = 'open') {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/positions?status=${status}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  }

  async createPosition(positionData: {
    recommendation_id: string;
    symbol: string;
    strategy: string;
    strike: number;
    expiration: string;
    option_symbol: string;
    contracts: number;
    entry_premium: number;
    price_at_entry: number;
    agent_position_id?: string;
    stock_entry_price?: number;
    current_stock_price?: number;
    cost_basis?: number;
  }) {
    const token = await this.getAuthToken();
    const response = await fetch(`${API_BASE_URL}/positions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(positionData),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  }

  async closePosition(positionId: string, exitPremium: number, exitStockPrice?: number) {
    const token = await this.getAuthToken();
    const payload: Record<string, number> = { exit_premium: exitPremium };
    if (exitStockPrice !== undefined) {
      payload.exit_stock_price = exitStockPrice;
    }
    const response = await fetch(`${API_BASE_URL}/positions/${positionId}/close`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
  }

}

export const userService = new UserService();
